/**
 * src/pages/CriticalThinking/sections/ArgumentReconstructorSection.tsx
 * Section 4: ARGUMENT RECONSTRUCTOR (id="argument-reconstructor")
 * At least 6 fictional everyday arguments:
 * advertisement, workplace decision, public discussion, scientific claim,
 * neighbourhood dispute, and family decision.
 * Steps:
 * 1. Identify conclusion
 * 2. Sort statements into premises vs side remarks
 * 3. Pick hidden assumptions
 * 4. Spot structural vulnerabilities / issues
 * 5. See reconstruction in standard form (P1, P2, conclusion)
 * 6. Strongest-reasonable steelman version
 * 7. How to improve the argument
 * Explicit teaching point: Criticism of argument vs criticism of the person.
 * Honest note: Values, uncertainty, and incomplete information also matter.
 */

import React, { useState } from "react";
import { KSection } from "@/components/knowledge/KSection";
import { ToolFrame } from "@/components/knowledge/ToolFrame";
import { Feedback } from "@/components/knowledge/Feedback";
import { Term } from "@/components/knowledge/Term";
import { ScopeNote } from "@/components/knowledge/ScopeNote";
import { ARGUMENT_CASES, type ReconstructedArgumentCase } from "../data/arguments.data";
import { Check, ChevronRight, ChevronLeft, RotateCcw, Sparkles, Layers, ArrowRight, ShieldCheck, HelpCircle, Lightbulb } from "lucide-react";
export function ArgumentReconstructorSection() {
  const [caseIndex, setCaseIndex] = useState(0);
  const currentCase = ARGUMENT_CASES[caseIndex];

  // User selections
  const [selectedConclusion, setSelectedConclusion] = useState<string | null>(null);
  const [selectedSentences, setSelectedSentences] = useState<Record<string, "premise" | "side">>({});
  const [selectedHiddenAssumption, setSelectedHiddenAssumption] = useState<string | null>(null);
  const [selectedVulnerability, setSelectedVulnerability] = useState<string | null>(null);

  // Toggle view of standard form and steelman
  const [showReconstruction, setShowReconstruction] = useState(false);
  const resetCase = () => {
    setSelectedConclusion(null);
    setSelectedSentences({});
    setSelectedHiddenAssumption(null);
    setSelectedVulnerability(null);
    setShowReconstruction(false);
  };
  const handleNextCase = () => {
    resetCase();
    setCaseIndex(prev => (prev + 1) % ARGUMENT_CASES.length);
  };
  const handlePrevCase = () => {
    resetCase();
    setCaseIndex(prev => (prev - 1 + ARGUMENT_CASES.length) % ARGUMENT_CASES.length);
  };
  const handleSentenceToggle = (sentenceId: string, role: "premise" | "side") => {
    setSelectedSentences(prev => ({
      ...prev,
      [sentenceId]: role
    }));
  };
  return <KSection id="argument-reconstructor" eyebrow="Interactive Instrument B" title="The Argument Reconstructor" intro="Everyday arguments rarely arrive formatted in tidy textbook syllogisms. They come wrapped in emotional rhetoric, side complaints, unstated assumptions, and conversational digressions. The Argument Reconstructor teaches you to isolate the core conclusion, filter out irrelevant remarks, reveal hidden premises, and formulate the strongest-possible steelman version.">
      <ToolFrame title="Everyday Reasoning Deconstruction & Steelmanning Studio" purpose="Deconstructs raw conversational disputes across six domains (workplace, advertising, civic debates, science, property disputes, and family advice) into standard premise-conclusion form while separating valid evidentiary critique from personal attacks." limits="Formal propositional logic alone does not settle real human disagreements. Values, priorities, uncertainty, risk tolerance, and incomplete information are legitimate dimensions of practical decision-making.">
        {/* Specimen Navigation Bar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pb-5 border-b border-border mb-6">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <span className="font-mono text-xs text-primary font-bold uppercase tracking-wider shrink-0">
              Dispute {caseIndex + 1} of {ARGUMENT_CASES.length}:
            </span>
            <span className="font-serif text-sm font-bold text-foreground break-words min-w-0">
              {currentCase.title}
            </span>
            <span className="text-[11px] font-mono text-muted-foreground px-2 py-0.5 rounded bg-card border border-border shrink-0">
              {currentCase.domain}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button type="button" onClick={handlePrevCase} className="p-2 rounded border border-border bg-card/60 hover:bg-card hover:border-primary/50 text-foreground transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center cursor-pointer" title="Previous argument" aria-label="Previous argument">
              <ChevronLeft className="size-4" />
            </button>
            <button type="button" onClick={handleNextCase} className="p-2 rounded border border-border bg-card/60 hover:bg-card hover:border-primary/50 text-foreground transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center cursor-pointer" title="Next argument" aria-label="Next argument">
              <ChevronRight className="size-4" />
            </button>
            <button type="button" onClick={resetCase} className="px-3 py-2 rounded border border-border bg-card/60 hover:bg-card hover:border-primary/50 text-xs font-mono text-muted-foreground hover:text-foreground transition-colors min-h-[44px] flex items-center gap-1.5 cursor-pointer">
              <RotateCcw className="size-3.5" />
              <span>Reset</span>
            </button>
          </div>
        </div>

        {/* Raw Passage Card */}
        <div className="p-4 sm:p-5 rounded-lg border border-border/80 bg-card/50 mb-6 space-y-3">
          <div className="text-xs font-mono text-muted-foreground flex items-center justify-between gap-2 flex-wrap min-w-0">
            <span className="shrink-0">CONVERSATIONAL CONTEXT</span>
            <span className="text-primary break-words text-right min-w-0">{currentCase.context}</span>
          </div>

          <blockquote className="p-4 rounded border-l-2 border-primary/70 bg-card/60 font-serif text-sm sm:text-base italic text-foreground leading-relaxed">
            "{currentCase.rawText}"
          </blockquote>
        </div>

        {/* Step 1: Identify the Conclusion */}
        <div className="space-y-6">
          <div className="p-4 rounded-lg border border-border/60 bg-card/20 space-y-2.5">
            <div className="flex items-center gap-2">
              <span className="crit-step-pill active">1</span>
              <h4 className="font-serif text-sm font-bold text-foreground">
                Step 1: Isolate the Conclusion
              </h4>
            </div>
            <p className="text-xs text-muted-foreground font-sans">
              What primary claim or action is the speaker actually trying to convince you to adopt?
            </p>

            <div className="space-y-2">
              {currentCase.conclusionOptions.map(opt => <button key={opt.id} type="button" onClick={() => setSelectedConclusion(opt.id)} className={`crit-option-btn ${selectedConclusion === opt.id ? "selected" : ""}`}>
                  <div className="size-4 rounded-full border border-border flex items-center justify-center shrink-0 mt-0.5">
                    {selectedConclusion === opt.id && <Check className="size-3 text-primary" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-foreground">{opt.text}</p>
                    {selectedConclusion === opt.id && <p className="text-xs text-primary/90 mt-1 font-mono">{opt.explanation}</p>}
                  </div>
                </button>)}
            </div>
          </div>

          {/* Step 2: Premise vs Side Remark Sorting */}
          <div className="p-4 rounded-lg border border-border/60 bg-card/20 space-y-3">
            <div className="flex items-center gap-2">
              <span className="crit-step-pill active">2</span>
              <h4 className="font-serif text-sm font-bold text-foreground">
                Step 2: Classify Statements — Premise vs Side Remark
              </h4>
            </div>
            <p className="text-xs text-muted-foreground font-sans">
              Sort each statement in the passage: is it an evidentiary <Term id="premise" /> intended to support the conclusion, or a rhetorical side remark / emotional digression?
            </p>

            <div className="space-y-3">
              {currentCase.sentences.map(sentence => {
              const currentChoice = selectedSentences[sentence.id];
              return <div key={sentence.id} className="p-3.5 rounded-lg border border-border/40 bg-card/40 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <p className="text-xs sm:text-sm text-foreground/90 flex-1 min-w-0 leading-relaxed break-words">
                      "{sentence.text}"
                    </p>

                    <div className="flex items-center gap-2 shrink-0">
                      <button type="button" onClick={() => handleSentenceToggle(sentence.id, "premise")} className={`px-3 py-1.5 rounded text-xs font-mono transition-colors min-h-[44px] flex items-center cursor-pointer ${currentChoice === "premise" ? "bg-primary text-background font-bold shadow-[0_0_8px_var(--glow-primary)]" : "border border-border bg-card/60 text-muted-foreground hover:text-foreground"}`}>
                        Premise
                      </button>
                      <button type="button" onClick={() => handleSentenceToggle(sentence.id, "side")} className={`px-3 py-1.5 rounded text-xs font-mono transition-colors min-h-[44px] flex items-center cursor-pointer ${currentChoice === "side" ? "bg-amber-400 text-background font-bold shadow-[0_0_8px_hsla(38,95%,50%,0.4)]" : "border border-border bg-card/60 text-muted-foreground hover:text-foreground"}`}>
                        Side Remark
                      </button>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          {/* Step 3: Spot Hidden Assumptions */}
          <div className="p-4 rounded-lg border border-border/60 bg-card/20 space-y-2.5">
            <div className="flex items-center gap-2">
              <span className="crit-step-pill active">3</span>
              <h4 className="font-serif text-sm font-bold text-foreground">
                Step 3: Uncover the Hidden Assumption
              </h4>
            </div>
            <p className="text-xs text-muted-foreground font-sans">
              Which unstated premise is logically required for the speaker's stated reasons to connect to their conclusion?
            </p>

            <div className="space-y-2">
              {currentCase.hiddenAssumptionOptions.map(opt => <button key={opt.id} type="button" onClick={() => setSelectedHiddenAssumption(opt.id)} className={`crit-option-btn ${selectedHiddenAssumption === opt.id ? "selected" : ""}`}>
                  <div className="size-4 rounded-full border border-border flex items-center justify-center shrink-0 mt-0.5">
                    {selectedHiddenAssumption === opt.id && <Check className="size-3 text-primary" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-foreground">{opt.text}</p>
                    {selectedHiddenAssumption === opt.id && <p className="text-xs text-primary/90 mt-1 font-mono">{opt.explanation}</p>}
                  </div>
                </button>)}
            </div>
          </div>

          {/* Step 4: Identify Structural Vulnerability */}
          <div className="p-4 rounded-lg border border-border/60 bg-card/20 space-y-2.5">
            <div className="flex items-center gap-2">
              <span className="crit-step-pill active">4</span>
              <h4 className="font-serif text-sm font-bold text-foreground">
                Step 4: Identify the Structural Vulnerability
              </h4>
            </div>
            <p className="text-xs text-muted-foreground font-sans">
              What is the deepest logical or evidentiary flaw in how this argument is framed?
            </p>

            <div className="space-y-2">
              {currentCase.vulnerabilityOptions.map(opt => <button key={opt.id} type="button" onClick={() => setSelectedVulnerability(opt.id)} className={`crit-option-btn ${selectedVulnerability === opt.id ? "selected" : ""}`}>
                  <div className="size-4 rounded-full border border-border flex items-center justify-center shrink-0 mt-0.5">
                    {selectedVulnerability === opt.id && <Check className="size-3 text-primary" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-foreground">{opt.text}</p>
                    {selectedVulnerability === opt.id && <p className="text-xs text-primary/90 mt-1 font-mono">{opt.explanation}</p>}
                  </div>
                </button>)}
            </div>
          </div>

          {/* Button to Reconstruct in Standard Form & Steelman */}
          <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
            <button type="button" onClick={() => setShowReconstruction(prev => !prev)} className="ql-neon-btn w-full sm:w-auto px-6 py-3 text-xs sm:text-sm flex items-center justify-center gap-2 cursor-pointer">
              <Layers className="size-4" />
              <span>{showReconstruction ? "Hide Reconstruction" : "Reconstruct in Standard Form & Steelman"}</span>
            </button>

            <button type="button" onClick={handleNextCase} className="ql-glass-btn w-full sm:w-auto px-5 py-2.5 text-xs font-mono text-foreground hover:text-primary flex items-center justify-center gap-2 cursor-pointer">
              <span>Next Dispute ({caseIndex + 2 > ARGUMENT_CASES.length ? 1 : caseIndex + 2})</span>
              <ArrowRight className="size-3.5" />
            </button>
          </div>

          {/* Reconstruction & Steelman Panel */}
          {showReconstruction && <div className="space-y-6 pt-4 border-t border-border/80">
              {/* Standard Form Box */}
              <div className="p-4 sm:p-5 rounded-lg border border-border/70 bg-card/50 space-y-3">
                <span className="font-mono text-xs font-bold text-primary uppercase block">
                  Reconstructed Standard Form (Numbered Premises & Conclusion)
                </span>

                <div className="space-y-2">
                  {currentCase.standardForm.premises.map((p, idx) => <div key={idx} className="crit-premise-row">
                      <span className="font-mono text-xs text-primary font-bold shrink-0">P{idx + 1}:</span>
                      <span className="text-foreground/90 min-w-0 break-words">{p}</span>
                    </div>)}

                  {currentCase.standardForm.hiddenPremises.map((hp, idx) => <div key={idx} className="crit-premise-row border-dashed border-amber-400/40 bg-amber-400/5">
                      <span className="font-mono text-xs text-amber-400 font-bold shrink-0">Unstated:</span>
                      <span className="text-foreground/90 italic min-w-0 break-words">{hp}</span>
                    </div>)}

                  <div className="crit-premise-row border-primary/60 bg-primary/10">
                    <span className="font-mono text-xs text-primary font-bold shrink-0">C:</span>
                    <span className="text-foreground font-semibold min-w-0 break-words">{currentCase.standardForm.conclusion}</span>
                  </div>
                </div>
              </div>

              {/* Steelmanned Version Box */}
              <div className="p-4 sm:p-5 rounded-lg border border-primary/50 bg-card/60 space-y-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="size-4 text-primary" />
                  <span className="font-mono text-xs font-bold text-primary uppercase">
                    Strongest-Reasonable (Steelman) Version
                  </span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Removing the hyperbole, personal grievances, and false dilemmas yields the most defensible version of the underlying concern:
                </p>

                <div className="space-y-2 p-3 rounded bg-card/80 border border-border/40">
                  {currentCase.steelmanVersion.premises.map((p, idx) => <p key={idx} className="text-xs text-foreground/90">
                      <strong className="text-primary font-mono">{idx + 1}.</strong> {p}
                    </p>)}
                  <p className="text-xs text-foreground font-semibold pt-1 border-t border-border/40">
                    <strong className="text-primary font-mono">Therefore:</strong> {currentCase.steelmanVersion.conclusion}
                  </p>
                </div>

                <p className="text-xs text-muted-foreground font-sans leading-relaxed">
                  {currentCase.steelmanVersion.explanation}
                </p>
              </div>

              {/* How to Improve the Weak Argument */}
              <div className="p-4 rounded-lg border border-border/60 bg-card/30 space-y-2">
                <span className="font-mono text-xs font-bold text-foreground flex items-center gap-1.5 uppercase">
                  <Lightbulb className="size-3.5 text-amber-400" />
                  How to Improve the Original Argument
                </span>
                <ul className="text-xs text-muted-foreground space-y-1.5 list-disc pl-4">
                  {currentCase.improvementWays.map((way, idx) => <li key={idx} className="leading-relaxed">
                      {way}
                    </li>)}
                </ul>
              </div>

              {/* Critical Note on Person vs Argument */}
              <Feedback tone="open" title="Crucial Discipline: Criticize the Argument, Not the Person">
                <p className="text-xs sm:text-sm text-foreground/90 leading-relaxed">
                  {currentCase.charityAndPersonNote}
                </p>
              </Feedback>
            </div>}
        </div>

        {/* Epistemic boundary reminder */}
        <ScopeNote className="mt-6">
          <strong>The Limits of Formal Logic:</strong> While reconstructive analysis reveals hidden leaps and fallacies, remember that formal validity alone does not settle complex human decisions. Values, priorities, ethical responsibilities, and subjective risk appetites are legitimate factors. A decision cannot be reduced solely to syllogisms when core human values conflict.
        </ScopeNote>
      </ToolFrame>
    </KSection>;
}